import { Box, Stack, Button, Divider, Typography } from '@mui/material'
import { Error } from 'components/Error'
import { Loading } from 'components/Loading'
import PageWrapper from 'hoc/PageWrapper'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { useLotQuery, useUpdateLotMutation } from 'services/lots'
import { StepsSection } from '../steps-section'
import { DocumentRequirementsSection } from 'pages/tender/lot/document-requirements-section'
import { TenderProposalForm } from './tender-proposal-form'
import { DeepPartial, FormProvider, useForm } from 'react-hook-form'
import { ILot } from 'types/lots'
import { CollapsibleSection } from 'components/CollapsibleSection'
import { useDispatch } from 'react-redux'
import { setLotData } from 'store/slice/lot'
import { mapFieldValuesToPayload } from './map-to-form-values'
import { GeneralLotInformation } from './general-lot-information'
import { toast } from 'react-toastify'
import { useTenderQuery } from 'services/tender'
import { formatTimeStampToDate } from 'hooks/date/formattedTime'
import { useEffect } from 'react'
import { ITender } from 'types/tender'
import { useAllProcurementRequirementsQuery } from 'services/lot/procurement-requirements'
import { useTenderPassedDocDeadline } from 'hooks/date/useTenderPassedDocDeadline'

type Props = {
  lot: ILot
  tenderData: ITender
}

const LotEdit = ({ lot, tenderData }: Props) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const { tenderId, lotId } = useParams<{ tenderId: string; lotId: string }>()

  const navigate = useNavigate()

  const [updateLot] = useUpdateLotMutation()
  const hasDocDeadlinePassed = useTenderPassedDocDeadline(tenderData)

  const editingDisabled = tenderData?.docSubmissionDeadline
    ? hasDocDeadlinePassed
    : false

  const { data: allProcurementRequirements } = useAllProcurementRequirementsQuery(
    { lotId: lotId ?? '' },
    { skip: !lotId }
  )

  const form = useForm<DeepPartial<ILot>>({
    mode: 'onChange',
    defaultValues: {
      ...lot,
      tenderId,
      id: lotId ?? '',
      procurementRequirement: {
        ...lot.procurementRequirement,
        validityDurationDays: lot.procurementRequirement?.validityDurationDays || 30,
      },
      technicalBidWeight: lot?.technicalBidWeight ?? 0,
      procurementBidWeight: lot?.procurementBidWeight ?? 0,
    },
  })

  const { handleSubmit, setValue } = form

  useEffect(() => {
    if (allProcurementRequirements) {
      setValue(
        'procurementRequirement.procurementRequirementItems',
        allProcurementRequirements?.procurementRequirementItems
      )
    }
  }, [allProcurementRequirements])

  const onSubmit = (values: DeepPartial<ILot>) => {
    if (lot && values.documentRequirements) {
      const updatedValues = mapFieldValuesToPayload(values, lot)
      const resultTechnicalBidWeight = updatedValues.technicalBidWeight
      const sumTechnicalBidWeight =
        updatedValues.technicalRequirement.technicalRequirementItems.reduce(
          (sum, item) => Number(item.point) + sum,
          0
        )
      if (sumTechnicalBidWeight > resultTechnicalBidWeight!) {
        toast.error(t('technical-bid-weight-error'))
        return
      }

      dispatch(setLotData(updatedValues))
      updateLot(updatedValues).then((res) => {
        if (!('error' in res)) {
          navigate(-1)
        } else {
          toast.error(t('failedToCreate'))
        }
      })
    }
  }

  return (
    <PageWrapper>
      <FormProvider {...form}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box
            bgcolor="white"
            py={2}
            px={1}
            zIndex={2}
            width="calc(100% + 16px)"
            position="sticky"
            top={-16}
            borderRadius={2}
            ml={-1}
          >
            <Box
              display="flex"
              alignItems="center"
              width="100%"
              justifyContent="space-between"
            >
              <Typography variant="h6" fontWeight="bolder" textTransform="uppercase">
                {t('lot')} - {lot?.name}
                {tenderData?.startTime
                  ? `, ${t('startDate')}: ${formatTimeStampToDate(tenderData?.startTime)}`
                  : ''}
              </Typography>

              <Box display="flex" gap={1}>
                <Button size="small" variant="outlined" onClick={() => navigate(-1)}>
                  {t('back')}
                </Button>
                {!editingDisabled && (
                  <Button
                    type="submit"
                    variant="contained"
                    style={{ color: '#fff', backgroundColor: 'rgb(3, 34, 76)' }}
                    color="primary"
                  >
                    {t('save')}
                  </Button>
                )}
              </Box>
            </Box>
          </Box>
          <Stack spacing={3}>
            <CollapsibleSection title={t('info-about-lot')}>
              <GeneralLotInformation
                lot={lot}
                isSubmissionTimePassed={editingDisabled}
              />
            </CollapsibleSection>

            <Divider flexItem />

            <StepsSection
              steps={[t('required-docs'), t('tender-proposal'), t('speciality')]}
            />

            <Divider flexItem />

            <CollapsibleSection title={t('required-docs')}>
              <DocumentRequirementsSection
                isSubmissionTimePassed={editingDisabled}
              />
            </CollapsibleSection>

            <Divider flexItem />

            <TenderProposalForm lot={lot} isSubmissionTimePassed={editingDisabled} />
          </Stack>
        </form>
      </FormProvider>
    </PageWrapper>
  )
}

export const LotEditPage = () => {
  const { lotId, tenderId } = useParams<{ lotId: string; tenderId: string }>()

  const { data, isLoading, isError } = useLotQuery({
    id: lotId ?? '',
  })

  const {
    data: tenderData,
    isLoading: isTenderLoading,
    isError: isTenderError,
  } = useTenderQuery({ id: tenderId ?? '' })

  if (isLoading || isTenderLoading) return <Loading />

  if (isError || isTenderError) return <Error />

  if (data === undefined || tenderData === undefined) {
    return <></>
  }

  return <LotEdit lot={data} tenderData={tenderData} />
}
